import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/marketing/MarketingChrome";
import { USE_CASES, CATEGORY_ORDER } from "@/data/useCases";
import { Seo } from "@/components/Seo";

export default function UseCases() {
  const byCat = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: USE_CASES.filter((u) => u.category === cat),
  })).filter((g) => g.items.length);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Seo
        title="What can Espresso produce?"
        description="A catalogue of everything Espresso can process and produce — knowledge bases, books, research compendiums, courses, engineering manuals, policy handbooks, encyclopedias and more."
        path="/use-cases"
      />
      <MarketingNav />

      <section className="border-b border-border grid-lines">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:py-24">
          <div className="mb-8 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            <span>Output Catalogue</span>
            <span className="h-px flex-1 bg-border" />
            <span>{USE_CASES.length} Output Types</span>
          </div>
          <h1 className="max-w-4xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            What can <span className="text-primary">Espresso</span> produce?
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            From a single prompt to an entire body of knowledge. Every output below runs through its own
            processing pipeline — ingest, structure, process, publish. Pick one and start the engine.
          </p>
        </div>
      </section>

      {byCat.map((group) => (
        <section key={group.cat} className="border-b border-border">
          <div className="mx-auto max-w-6xl px-6 py-12">
            <div className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-primary">{group.cat}</div>
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-sm border border-border bg-border md:grid-cols-2">
              {group.items.map((u, i) => (
                <motion.div
                  key={u.slug}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                >
                  <Link
                    to={`/use-cases/${u.slug}`}
                    data-testid="use-case-specimen-card"
                    className="group flex h-full flex-col bg-card p-8 transition-colors hover:bg-secondary"
                  >
                    <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      <span>{u.ref}</span>
                      <span>{u.kind}</span>
                    </div>
                    <h3 className="mt-6 font-display text-3xl font-semibold leading-tight tracking-tight">{u.label}</h3>
                    <p className="mt-3 flex-1 leading-relaxed text-muted-foreground">{u.excerpt}</p>
                    <div className="mt-6 flex flex-wrap gap-1.5">
                      {u.pipeline.slice(0, 4).map((p) => (
                        <span key={p} className="rounded-sm border border-border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                          {p}
                        </span>
                      ))}
                      <span className="px-1 font-mono text-[10px] text-muted-foreground">+{u.pipeline.length - 4}</span>
                    </div>
                    <span className="mt-6 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-primary">
                      View output
                      <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="bg-secondary">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-6 py-16 md:flex-row md:items-center">
          <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Knowledge deserves an engine.</h2>
          <Link to="/register">
            <Button size="lg" className="rounded-sm px-8" data-testid="usecases-cta-button">
              Begin <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
